/**
 * `lazy()` that survives a deploy happening mid-session.
 *
 * When a new build is deployed, the old hashed chunks (e.g. `PlayPage-a1b2c3.js`)
 * are deleted from the CDN. A returning user still running the previous `index.html`
 * then navigates to a `lazy()` route and the dynamic `import()` 404s with
 * "Failed to fetch dynamically imported module" — which otherwise surfaces as the
 * generic ErrorBoundary "something went wrong" screen (see nginx `open() … failed`
 * 404s for stale `assets/*.js` in the access log).
 *
 * A second, more common failure mode showed up on mobile in-app browsers (iOS
 * WKWebView — Rabby/Telegram/etc.): the module fetch fails *transiently* and never even
 * reaches the server. On 2026-07-30 a /pvp-trial crash showed "Importing a module script
 * failed." with NO request for `PvpTrialPage-*.js` in the nginx access log and no 404,
 * seconds after other chunks had loaded fine. A reload does not help there — the retry
 * does.
 *
 * So recovery is layered:
 *   1. Retry the dynamic import a couple of times with a short backoff (transient webview
 *      fetch failure — the chunk exists, the fetch just died).
 *   2. Still failing? Force a full page reload so the browser fetches the fresh
 *      `index.html` and its valid chunk hashes (stale chunk after a deploy). Up to
 *      `MAX_CHUNK_RELOADS` reloads per chunk inside `CHUNK_RELOAD_WINDOW_MS`, each one after
 *      a longer pause: a reload landing INSIDE a deploy's switch-over can still get the old
 *      `index.html` and fail again, and the second, later reload rides that window out.
 *   3. Still failing after that? Rethrow so the ErrorBoundary handles it — a sessionStorage
 *      record (count + time of the first reload) guards against a reload loop.
 * The record is cleared once the chunk does load, and a record older than the window is
 * ignored: on 2026-11-01 (Beta 21.85, two deploys that day) a player on /changelog got the
 * error screen for a stale `SpeciesPage` chunk instead of a reload. The old guard was a
 * plain "already reloaded once this session" flag, which allowed exactly one try per tab
 * for the whole session.
 */
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

export const CHUNK_RELOAD_FLAG_PREFIX = 'suikamon.chunkReload.v2:';
const RELOAD_FLAG_PREFIX = CHUNK_RELOAD_FLAG_PREFIX;

/** Reloads allowed for one chunk inside `CHUNK_RELOAD_WINDOW_MS` before the error is shown. */
export const MAX_CHUNK_RELOADS = 2;
/** A reload record older than this is stale (an earlier deploy) and no longer blocks recovery. */
export const CHUNK_RELOAD_WINDOW_MS = 5 * 60 * 1000;
/** Pause before each reload, by reload number: the first is immediate, the second waits out a deploy switch-over. */
export const CHUNK_RELOAD_DELAYS_MS = [0, 4000];

interface ChunkReloadRecord {
  count: number;
  firstAt: number;
}

function readReloadRecord(flag: string, now: number): ChunkReloadRecord | null {
  try {
    const raw = window.sessionStorage.getItem(flag);
    if (!raw) return null;
    const record = JSON.parse(raw) as Partial<ChunkReloadRecord>;
    if (typeof record.count !== 'number' || typeof record.firstAt !== 'number') return null;
    if (now - record.firstAt > CHUNK_RELOAD_WINDOW_MS) return null;
    return { count: record.count, firstAt: record.firstAt };
  } catch {
    return null;
  }
}

/**
 * Claims one reload for `chunkKey` if the guard allows it and returns the pause before
 * reloading, or `null` when the reloads are used up (the caller shows the error instead).
 */
export function claimChunkReload(chunkKey: string, now: number = Date.now()): number | null {
  if (typeof window === 'undefined') return null;
  const flag = RELOAD_FLAG_PREFIX + chunkKey;
  const previous = readReloadRecord(flag, now);
  const used = previous?.count ?? 0;
  if (used >= MAX_CHUNK_RELOADS) return null;
  try {
    window.sessionStorage.setItem(flag, JSON.stringify({ count: used + 1, firstAt: previous?.firstAt ?? now }));
  } catch {
    // No sessionStorage (private mode / blocked): without a guard a reload could loop.
    return null;
  }
  return CHUNK_RELOAD_DELAYS_MS[used] ?? 0;
}

/** Reloads the page after `delayMs` (0 = now). */
export function reloadPageAfter(delayMs: number): void {
  if (delayMs > 0) {
    window.setTimeout(() => window.location.reload(), delayMs);
  } else {
    window.location.reload();
  }
}

/** Backoff before each retry of a failed chunk import. Length = number of retries. */
export const IMPORT_RETRY_DELAYS_MS = [300, 900];

/** A dynamic-import failure caused by a missing/stale chunk (not a runtime error inside the module). */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    // Vite's preload helper, when a lazy route's stylesheet is gone after a deploy.
    /Unable to preload CSS/i.test(message) ||
    // Legacy webpack-style name still emitted by some browsers/bundlers.
    (error instanceof Error && error.name === 'ChunkLoadError')
  );
}

/**
 * The catch handler for a lazy `import()`: reload once on a stale-chunk error,
 * otherwise rethrow. Exported (and window-agnostic) so it's unit-testable without
 * a real `lazy()`/Suspense render.
 */
export function handleChunkImportFailure<T>(error: unknown, chunkKey: string): Promise<{ default: T }> {
  if (typeof window === 'undefined' || !isChunkLoadError(error)) {
    throw error;
  }
  const delayMs = claimChunkReload(chunkKey);
  if (delayMs === null) {
    // Reloads for this chunk are used up — don't loop.
    throw error;
  }
  reloadPageAfter(delayMs);
  // Never-resolving promise so React keeps the Suspense fallback during the
  // (imminent) reload instead of flashing an error.
  return new Promise<{ default: T }>(() => {});
}

/** Releases the reload guard once the chunk has actually loaded. */
export function clearChunkReloadFlag(chunkKey: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(RELOAD_FLAG_PREFIX + chunkKey);
  } catch {
    // No sessionStorage — nothing was recorded.
  }
}

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

/**
 * Runs a dynamic `import()` and retries it on a chunk-load failure, which on mobile
 * in-app webviews is usually a transient fetch death rather than a missing chunk.
 * Runtime errors thrown from inside the module are NOT retried — they'd just fail again
 * and re-run any module side effects.
 */
export async function importWithRetry<T>(
  factory: () => Promise<{ default: T }>,
  delaysMs: readonly number[] = IMPORT_RETRY_DELAYS_MS,
): Promise<{ default: T }> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= delaysMs.length; attempt++) {
    if (attempt > 0) await sleep(delaysMs[attempt - 1]);
    try {
      return await factory();
    } catch (error) {
      if (!isChunkLoadError(error)) throw error;
      lastError = error;
    }
  }
  throw lastError;
}

// Mirrors React's own `lazy` typing (`ComponentType<any>`) so the wrapped
// component's props are preserved through the wrapper.
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  chunkKey: string,
): LazyExoticComponent<T> {
  return lazy(() =>
    importWithRetry(factory)
      .then(module => {
        clearChunkReloadFlag(chunkKey);
        return module;
      })
      .catch((error: unknown) => handleChunkImportFailure<T>(error, chunkKey)),
  );
}
